import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Truck, Check, ArrowRight } from "lucide-react";

const shops = ["Rhodes Shopping Centre", "Ashfield Mall", "Charlestown Square", "Newcastle"];
// 30-min slots from 18:30 to 21:00
const timeSlots = ["18:30", "19:00", "19:30", "20:00", "20:30", "21:00"];

export default function DryCleaningPickup() {
  const nav = useNavigate();
  const [state, setState] = useState({
    full_name: "", mobile: "", email: "",
    pickup_address: "", suburb_postcode: "",
    pickup_date: "", pickup_time: "",
    shop: shops[0], item_types: "", estimated_items: 1, notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit =
    state.full_name && state.mobile && state.pickup_address &&
    state.suburb_postcode && state.pickup_date && state.pickup_time &&
    state.shop && state.item_types && state.estimated_items > 0;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...state, estimated_items: Number(state.estimated_items) };
      if (!payload.email) delete payload.email;
      const { data } = await api.post("/pickups", payload);
      setResult(data);
      toast.success("Pickup request received!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-[#0F172A] text-[#FFFFFF] flex items-center justify-center mx-auto"><Check size={28}/></div>
        <div className="overline mt-8">Pickup confirmed</div>
        <h1 className="font-serif-display text-5xl mt-4">See you soon.</h1>
        <div className="mt-8 inline-block border border-[#E2E8F0] p-8 bg-[#F8FAFC]">
          <div className="overline">Your reference</div>
          <div data-testid="pickup-code" className="font-serif-display text-4xl mt-3">{result.pickup_code}</div>
        </div>
        <p className="mt-8 text-[#4A4A4A] max-w-md mx-auto">Our driver will call you before arrival. Please have your items ready at the pickup address.</p>
        <button onClick={() => nav("/")} className="mt-10 px-8 py-3 bg-[#D4AF37] text-[#0F172A] text-xs uppercase tracking-[0.25em] hover:bg-[#B8941F]">Back to Home</button>
      </div>
    );
  }

  const input = (k, label, opts = {}) => (
    <div className={opts.wide ? "md:col-span-2" : ""}>
      <label className="overline">{label}{opts.required && " *"}</label>
      <input data-testid={`pickup-${k}`} type={opts.type || "text"} value={state[k]}
             onChange={(e) => setState({ ...state, [k]: e.target.value })}
             placeholder={opts.placeholder || ""} min={opts.min}
             className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none"/>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline flex items-center gap-2"><Truck size={12}/> Dry Cleaning</div>
      <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Book a pickup</h1>
      <p className="mt-4 text-[#4A4A4A] max-w-lg">Fill out the form and our driver will collect your items from your door — evenings only, from 6:30pm.</p>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {input("full_name", "Full name", { required: true })}
        {input("mobile", "Mobile number", { required: true, type: "tel", placeholder: "+61 4xx xxx xxx" })}
        {input("email", "Email (optional)", { type: "email", wide: true })}
        {input("pickup_address", "Pickup address", { required: true, wide: true, placeholder: "Street, unit no." })}
        {input("suburb_postcode", "Suburb & postcode", { required: true })}

        <div>
          <label className="overline">Nearest shop *</label>
          <select data-testid="pickup-shop" value={state.shop}
                  onChange={(e) => setState({ ...state, shop: e.target.value })}
                  className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none">
            {shops.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {input("pickup_date", "Pickup date", { required: true, type: "date", min: new Date().toISOString().slice(0, 10) })}

        <div>
          <label className="overline">Pickup time * (after 6:30 PM)</label>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {timeSlots.map(t => (
              <button key={t} type="button" data-testid={`pickup-time-${t.replace(":","")}`}
                      onClick={() => setState({ ...state, pickup_time: t })}
                      className={`py-3 text-sm border ${state.pickup_time === t ? "bg-[#0F172A] text-[#FFFFFF] border-[#0F172A]" : "border-[#E2E8F0] hover:border-[#0F172A]"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {input("item_types", "Type of items", { required: true, wide: true, placeholder: "e.g. 2 shirts, 1 suit, blanket" })}
        {input("estimated_items", "Estimated number of items", { required: true, type: "number", min: 1 })}

        <div className="md:col-span-2">
          <label className="overline">Special instructions or notes</label>
          <textarea data-testid="pickup-notes" rows={3} value={state.notes}
                    onChange={(e) => setState({ ...state, notes: e.target.value })}
                    placeholder="Gate code, buzzer, doorman, etc."
                    className="mt-2 w-full border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-3 outline-none"/>
        </div>
      </div>

      <button data-testid="pickup-submit" onClick={submit} disabled={!canSubmit || submitting}
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0F172A] text-xs uppercase tracking-[0.25em] hover:bg-[#B8941F] disabled:opacity-30">
        {submitting ? "Sending..." : "Request pickup"} <ArrowRight size={14}/>
      </button>
    </div>
  );
}
